import { ArrowLeft, ArrowRight, BookOpen, ExternalLink } from 'lucide-react'
import { AGENTS } from '@/components/onboarding-sheet/agents'
import { useOnboardingSheet } from '@/components/onboarding-sheet/context'
import { Button } from '@/components/ui/button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import {
  ClaudeCodeLogo,
  CodexLogo,
  CursorLogo,
  OpenCodeLogo,
  WindsurfLogo,
} from '@/logos'
import type { AgentId } from '@/components/onboarding-sheet/agents'

const AGENT_LOGOS: Record<
  AgentId,
  React.ComponentType<{ className?: string }>
> = {
  'claude-code': ClaudeCodeLogo,
  cursor: CursorLogo,
  windsurf: WindsurfLogo,
  opencode: OpenCodeLogo,
  codex: CodexLogo,
}

export const AgentSelectView = () => {
  const { setView, setSelectedAgent, setSelectedPath } = useOnboardingSheet()

  const handleBack = () => {
    setView('cta')
  }

  const handleSelect = (agentId: AgentId) => {
    setSelectedPath('agent')
    setSelectedAgent(agentId)
    setView('steps')
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto p-6 space-y-6">
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="-ml-2 self-start text-muted-foreground"
        >
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <div>
          <h2 className="text-lg font-semibold">Connect your AI agent</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choose the AI coding agent you want to connect to Sevalla
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {AGENTS.map((agent) => {
          const Logo = AGENT_LOGOS[agent.id]
          return (
            <Item
              key={agent.id}
              asChild
              variant="outline"
              className="cursor-pointer rounded-xl text-left hover:bg-accent/50 hover:shadow-sm"
            >
              <button onClick={() => handleSelect(agent.id)}>
                <ItemMedia className="size-10 rounded-xl border border-border bg-muted">
                  <Logo className="size-5" />
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>{agent.name}</ItemTitle>
                  <ItemDescription>{agent.description}</ItemDescription>
                </ItemContent>
                <ItemActions>
                  <span className="text-xs text-muted-foreground">
                    {agent.steps.length} steps
                  </span>
                  <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover/item:translate-x-1" />
                </ItemActions>
              </button>
            </Item>
          )
        })}

        <Item
          asChild
          variant="outline"
          className="cursor-pointer rounded-xl text-left hover:bg-accent/50 hover:shadow-sm"
        >
          <a
            href="https://docs.sevalla.com/quick-starts/coding-agents/overview"
            target="_blank"
            rel="noreferrer"
          >
            <ItemMedia className="size-10 rounded-xl border border-border bg-muted">
              <BookOpen className="size-5 text-muted-foreground" />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>Using a different agent?</ItemTitle>
              <ItemDescription>
                View setup docs for any MCP-compatible coding agent.
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <ExternalLink className="size-4 text-muted-foreground" />
            </ItemActions>
          </a>
        </Item>
      </div>
    </div>
  )
}
